/**
 * Styles for ScopeCard component
 * @module components/ScopeCard/styles
 */
import { Theme, alpha } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';

/**
 * Styles hook for ScopeCard component
 * @param theme - MUI theme
 * @returns Style classes
 */
// @ts-ignore
const useStyles = makeStyles<Theme>((theme) => ({
  card: {
    width: theme.typography.pxToRem(320),
    height: theme.typography.pxToRem(252),
    display: 'flex',
    flexDirection: 'column',
    cursor: 'pointer',
    transition: theme.transitions.create(['transform', 'box-shadow', 'border-color'], {
      duration: theme.transitions.duration.standard,
    }),
    position: 'relative',
    borderRadius: theme.typography.pxToRem(8),
    backgroundColor: theme.palette.common.white,
    padding: `${theme.spacing(2)} ${theme.spacing(3)}`,
    gap: theme.spacing(2), // 16px
    // Default styles (will be overridden by color variants)
    border: `1px solid ${theme.palette.grey[400]}`, // #A3A3AF equivalent
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: theme.shadows[4],
    },
  },
  cardPrimary: {
    border: `2px solid ${theme.palette.error.main}`, // #F04E4A
    boxShadow: `0px 8px 20px 0px ${alpha(theme.palette.grey[500], 0.3)}`, // #CECDD1 equivalent
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: `0px 12px 24px 0px ${alpha(theme.palette.grey[500], 0.4)}`,
      borderColor: theme.palette.error.main,
    },
  },
  cardDefault: {
    border: `1px solid ${theme.palette.grey[400]}`, // #A3A3AF
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: theme.shadows[4],
      borderColor: theme.palette.grey[400],
    },
  },
  cardContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'end',
    textAlign: 'center',
    height: '100%',
    width: '100%',
    padding: `0 0 ${theme.spacing(2)} 0 !important`, // Remove default CardContent padding since we handle it in card
  },
  iconContainer: {
    marginBottom: theme.spacing(2), // 16px spacing
  },
  icon: {
    width: theme.typography.pxToRem(114), // Tamaño del ícono más grande
    height: theme.typography.pxToRem(78),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '& svg': {
      width: '100%',
      height: '100%',
      color: theme.palette.primary.main,
    },
  },
  iconPrimary: {
    '& svg': {
      color: theme.palette.primary.main,
    },
  },
  iconDefault: {
    '& svg': {
      color: theme.palette.grey[600],
    },
  },
  cardTitle: {
    fontFamily: theme.typography.fontFamily,
    fontWeight: theme.typography.fontWeightMedium, // 600 equivalent
    fontSize: theme.typography.pxToRem(20),
    lineHeight: theme.typography.pxToRem(24),
    letterSpacing: 0,
    textAlign: 'center',
    color: theme.palette.text.secondary, // #47465F from theme
    marginBottom: theme.spacing(1),
  },
  cardDescription: {
    fontFamily: theme.typography.fontFamily,
    fontWeight: theme.typography.fontWeightRegular,
    fontSize: theme.typography.pxToRem(14),
    lineHeight: theme.typography.pxToRem(18),
    letterSpacing: 0,
    textAlign: 'center',
    color: theme.palette.text.secondary, // #47465F from theme
  },
  // Primary variant text styles
  cardTitlePrimary: {
    color: theme.palette.common.black,
  },
  cardDescriptionPrimary: {
    color: theme.palette.common.black,
  },
  disabledCard: {
    opacity: 0.5,
    cursor: 'not-allowed',
    '&:hover': {
      transform: 'none',
      // Don't override box-shadow or border - let the variant styles show through with opacity
    },
  },

  recommendedBadge: {
    position: 'absolute',
    top: theme.spacing(1.5),
    right: theme.spacing(1.5),
    backgroundColor: theme.palette.secondary.main,
    color: theme.palette.common.white,
    padding: theme.spacing(0.5, 1.5),
    borderRadius: theme.spacing(2),
    fontSize: theme.typography.pxToRem(12), // 0.75rem equivalent
    fontWeight: theme.typography.fontWeightMedium,
    zIndex: 1,
    boxShadow: theme.shadows[2],
  },
}));

export default useStyles;
